/**
 * Production Capital Share Campaign (P1-P5 matrix).
 *
 * Financial safety notes:
 * - Package amounts come only from this matrix (no linear/tier derivation).
 * - `paidShareCap` is enforced on paid shares; inventory checks use paid+bonus+sponsor.
 * - Gifts remain separately kill-switched in approval paths.
 */

/** @typedef {'P1' | 'P2' | 'P3' | 'P4' | 'P5'} ProductionCapitalPackageId */

export const PRODUCTION_CAPITAL_CAMPAIGN = {
  id: 'PRODUCTION_CAPITAL_2026',
  name: 'Production Capital Share Campaign',
  pricePerPaidShareUsd: 100,
  maxPackageQty: 100,
  paidShareCap: 250000,
  startingActualInventory: 797562,
  // Go-live: Sunday 2026-09-13 23:59:59 SAST (special active from Mon 2026-09-14)
  // Close: Thursday 2026-11-05 23:59:59 SAST (21:59:59 UTC)
  startDate: '2026-09-13T21:59:59Z',
  endDate: '2026-11-05T21:59:59Z',
  markers: {
    buyerGift: 'PCAP_2026_BUYER_GIFT',
    uplineGift: 'PCAP_2026_UPLINE_GIFT',
  },
  sessionKeys: {
    packageId: 'aureus.pcapPackage',
    packageQty: 'aureus.pcapQty',
  },
};

/** @type {Record<ProductionCapitalPackageId, { id: ProductionCapitalPackageId, name: string, paidShares: number, priceUsd: number, investorBonus: number, sponsorBonus: number, active: boolean, sortOrder: number }>} */
export const PRODUCTION_CAPITAL_PACKAGES = {
  P1: { id: 'P1', name: 'Package 1', paidShares: 1, priceUsd: 100, investorBonus: 1, sponsorBonus: 0.5, active: true, sortOrder: 1 },
  P2: { id: 'P2', name: 'Package 2', paidShares: 5, priceUsd: 500, investorBonus: 6, sponsorBonus: 2.5, active: true, sortOrder: 2 },
  P3: { id: 'P3', name: 'Package 3', paidShares: 10, priceUsd: 1000, investorBonus: 13, sponsorBonus: 5, active: true, sortOrder: 3 },
  P4: { id: 'P4', name: 'Package 4', paidShares: 25, priceUsd: 2500, investorBonus: 35, sponsorBonus: 12.5, active: true, sortOrder: 4 },
  P5: { id: 'P5', name: 'Package 5', paidShares: 50, priceUsd: 5000, investorBonus: 75, sponsorBonus: 25, active: true, sortOrder: 5 },
};

/**
 * @param {unknown} id
 * @returns {id is ProductionCapitalPackageId}
 */
export function isProductionCapitalPackageId(id) {
  return id === 'P1' || id === 'P2' || id === 'P3' || id === 'P4' || id === 'P5';
}

const PCAP_AMOUNT_TOL = 0.51

/**
 * Infer P1–P5 + qty from paid shares and USD amount.
 * Prefer an official package at qty=1 (P5→P1) so 5/$500 is P2, not 5×P1.
 * Otherwise accept integer P1 multiples (2/$200 → P1 qty 2).
 *
 * @param {{ paidShares: unknown, amountUsd: unknown }} args
 * @returns {{ packageId: ProductionCapitalPackageId, qty: number } | null}
 */
export function inferProductionCapitalPackage({ paidShares, amountUsd }) {
  const shares = Number(paidShares)
  const amt = Number(amountUsd)
  if (!Number.isFinite(shares) || shares <= 0 || !Number.isFinite(amt) || amt <= 0) return null

  const official = listActiveProductionCapitalPackages().slice().sort((a, b) => b.paidShares - a.paidShares)
  for (const pkg of official) {
    if (shares === pkg.paidShares && Math.abs(amt - pkg.priceUsd) < PCAP_AMOUNT_TOL) {
      return { packageId: pkg.id, qty: 1 }
    }
  }

  const p1 = getProductionCapitalPackage('P1')
  if (!p1) return null
  const qty = shares / p1.paidShares
  if (!Number.isInteger(qty) || qty < 1 || qty > (PRODUCTION_CAPITAL_CAMPAIGN.maxPackageQty || 100)) return null
  if (Math.abs(amt - p1.priceUsd * qty) < PCAP_AMOUNT_TOL) {
    const p1Bonus = computeProductionCapitalBreakdown('P1', qty)
    const denser = optimizeProductionCapitalSpend(amt)
    if (
      denser
      && denser.lines.length === 1
      && denser.paidShares === shares
      && p1Bonus
      && denser.investorBonus > p1Bonus.investorBonus
    ) {
      return { packageId: denser.lines[0].packageId, qty: denser.lines[0].qty }
    }
    return { packageId: 'P1', qty }
  }
  return null
}

/**
 * A P1×N stamp that optimizeProductionCapitalSpend can beat becomes that cart.
 * One-line carts (20/$2000 → 2×P3) stay a single campaign_package.
 * Multi-line carts ($800/$1200/$1900) become campaign_cart_explicit.
 */
function densifyP1MultipleNotes(next, inferred, paid, amt) {
  const p1Bonus = computeProductionCapitalBreakdown('P1', inferred.qty)
  const denser = optimizeProductionCapitalSpend(amt)
  if (
    !p1Bonus
    || !denser
    || denser.investorBonus <= p1Bonus.investorBonus
    || denser.paidShares !== paid
    || Math.abs(denser.costUsd - amt) > PCAP_AMOUNT_TOL
  ) {
    return null
  }
  const lines = denser.lines.map((line) => ({ packageId: line.packageId, qty: line.qty }))
  if (isProductionCapitalPackageId(next.campaign_package)) {
    next.pcap_canonicalized_from = {
      packageId: next.campaign_package,
      qty: Math.max(1, Math.floor(Number(next.campaign_package_qty) || 1)),
    }
  }
  next.campaign_id = PRODUCTION_CAPITAL_CAMPAIGN.id
  if (lines.length === 1) {
    next.campaign_package = lines[0].packageId
    next.campaign_package_qty = lines[0].qty
    return { notes: next, changed: true, inferred: { packageId: lines[0].packageId, qty: lines[0].qty } }
  }
  next.campaign_cart_explicit = true
  next.campaign_packages = lines
  next.campaign_package = lines[0].packageId
  next.campaign_package_qty = lines[0].qty
  return { notes: next, changed: true, inferred }
}

/**
 * Force notes onto the official qty=1 package when paid shares/amount match
 * P2–P5. Stops P1×5 ($500) from issuing 5 buyer bonus instead of Package 2's 6.
 *
 * @param {Record<string, any>|string|null|undefined} notes
 * @param {{ paidShares: unknown, amountUsd: unknown }} args
 */
export function canonicalizeProductionCapitalNotes(notes, { paidShares, amountUsd } = {}) {
  let base = notes
  if (typeof base === 'string') {
    try { base = JSON.parse(base) } catch { base = {} }
  }
  const next = base && typeof base === 'object' && !Array.isArray(base) ? { ...base } : {}
  if (next.campaign_package_chosen === true) {
    const qty = Math.max(1, Math.floor(Number(next.campaign_package_qty) || 1))
    if (next.campaign_kind === 'standard') {
      return { notes: next, changed: false, inferred: null }
    }
    if (isProductionCapitalPackageId(next.campaign_package)) {
      return { notes: next, changed: false, inferred: { packageId: next.campaign_package, qty } }
    }
  }
  if (next.campaign_cart_explicit === true) {
    return { notes: next, changed: false, inferred: null }
  }
  let paid = Number(paidShares)
  let amt = Number(amountUsd)
  if ((!Number.isFinite(paid) || paid <= 0 || !Number.isFinite(amt) || amt <= 0) && isProductionCapitalPackageId(next.campaign_package)) {
    const stamped = computeProductionCapitalBreakdown(next.campaign_package, next.campaign_package_qty || 1)
    if (stamped) {
      if (!Number.isFinite(paid) || paid <= 0) paid = stamped.paidShares
      if (!Number.isFinite(amt) || amt <= 0) amt = stamped.priceUsd
    }
  }
  const inferred = inferProductionCapitalPackage({ paidShares: paid, amountUsd: amt })
  if (!inferred) return { notes: next, changed: false, inferred: null }
  if (inferred.packageId === 'P1' && inferred.qty > 1) {
    const densified = densifyP1MultipleNotes(next, inferred, paid, amt)
    if (densified) return densified
  }
  const currentId = next.campaign_package
  const currentQty = Math.max(1, Math.floor(Number(next.campaign_package_qty) || 1))
  if (currentId === inferred.packageId && currentQty === inferred.qty) {
    return { notes: next, changed: false, inferred }
  }
  if (isProductionCapitalPackageId(currentId)) {
    next.pcap_canonicalized_from = { packageId: currentId, qty: currentQty }
  }
  next.campaign_id = PRODUCTION_CAPITAL_CAMPAIGN.id
  next.campaign_package = inferred.packageId
  next.campaign_package_qty = inferred.qty
  return { notes: next, changed: true, inferred }
}

/**
 * Admin-chosen package on a manual bank or crypto create. Skips densify.
 * Standard means no PCAP stamp. P1–P5 keeps that exact package and qty.
 *
 * @param {Record<string, any>} notes
 * @param {{ packageKind?: 'standard' | 'pcap', packageId?: unknown, qty?: unknown }} args
 */
export function applyChosenManualPackage(notes, { packageKind, packageId, qty } = {}) {
  const next = notes && typeof notes === 'object' ? { ...notes } : {}
  next.campaign_package_chosen = true
  if (packageKind === 'standard') {
    next.campaign_kind = 'standard'
    delete next.campaign_package
    delete next.campaign_package_qty
    delete next.campaign_id
    delete next.campaign_packages
    delete next.campaign_cart_explicit
    return { notes: next, chosen: true, inferred: null }
  }
  const q = Math.max(1, Math.floor(Number(qty) || 1))
  if (!isProductionCapitalPackageId(packageId)) {
    return { notes: next, chosen: false, inferred: null, error: 'Choose a Production Capital package or Standard.' }
  }
  const breakdown = computeProductionCapitalBreakdown(packageId, q)
  if (!breakdown) {
    return { notes: next, chosen: false, inferred: null, error: 'Invalid Production Capital package quantity.' }
  }
  next.campaign_kind = 'pcap'
  next.campaign_id = PRODUCTION_CAPITAL_CAMPAIGN.id
  next.campaign_package = packageId
  next.campaign_package_qty = q
  return { notes: next, chosen: true, inferred: { packageId, qty: q }, breakdown }
}

/**
 * Stamp campaign_package onto admin manual-entry notes when the campaign
 * window is open and paid shares/amount match a matrix package. Overwrites a
 * non-canonical stamp (P1×5 on 5/$500 → P2) so gifts follow the official matrix
 * unless campaign_package_chosen is already set.
 *
 * @param {Record<string, any>} notes
 * @param {{ paidShares: unknown, amountUsd: unknown, now?: Date }} args
 */
export function stampManualPaymentPcapNotes(notes, { paidShares, amountUsd, now } = {}) {
  const next = notes && typeof notes === 'object' ? { ...notes } : {}
  if (next.campaign_package_chosen === true) {
    if (next.campaign_kind === 'standard') {
      return { notes: next, stamped: false, inferred: null, already: true, chosen: true }
    }
    if (isProductionCapitalPackageId(next.campaign_package)) {
      const qty = Math.max(1, Math.floor(Number(next.campaign_package_qty) || 1))
      return { notes: next, stamped: false, inferred: { packageId: next.campaign_package, qty }, already: true, chosen: true }
    }
  }
  if (!isProductionCapitalDateActive(now instanceof Date ? now : new Date())) {
    return { notes: next, stamped: false, inferred: null }
  }
  const hadPackage = isProductionCapitalPackageId(next.campaign_package)
  const canon = canonicalizeProductionCapitalNotes(next, { paidShares, amountUsd })
  if (!canon.inferred) {
    return { notes: next, stamped: false, inferred: null, already: hadPackage }
  }
  if (!canon.changed) {
    return { notes: canon.notes, stamped: false, inferred: canon.inferred, already: hadPackage }
  }
  return { notes: canon.notes, stamped: true, inferred: canon.inferred }
}

function summarizePcapGiftLeg(leg) {
  if (!leg || typeof leg !== 'object') return null
  return {
    ok: !!leg.ok,
    skipped: !!leg.skipped,
    error: leg.error || null,
    reference: leg.reference || null,
    purchaseId: leg.purchaseId || null,
    bonusShares: leg.bonusShares ?? null,
    sponsorId: leg.sponsorId ?? null,
  }
}

/**
 * Merge a compact PCAP gift audit onto payment notes. Does not drop other keys.
 * @param {Record<string, any>|string|null|undefined} notes
 * @param {Record<string, any>} audit
 */
export function mergePcapGiftAudit(notes, audit) {
  let base = notes
  if (typeof base === 'string') {
    try { base = JSON.parse(base) } catch { base = {} }
  }
  const next = base && typeof base === 'object' && !Array.isArray(base) ? { ...base } : {}
  const src = audit && typeof audit === 'object' ? audit : {}
  const breakdown = src.breakdown && typeof src.breakdown === 'object' ? src.breakdown : null
  next.pcap_gift_audit = {
    at: new Date().toISOString(),
    live: !!src.live,
    applicable: !!src.applicable,
    warnings: Array.isArray(src.warnings) ? src.warnings.slice(0, 12) : [],
    error: src.error || null,
    buyer: summarizePcapGiftLeg(src.buyer),
    upline: summarizePcapGiftLeg(src.upline),
    breakdown: breakdown ? {
      packageId: breakdown.packageId || null,
      qty: breakdown.qty ?? null,
      paidShares: breakdown.paidShares ?? null,
      investorBonus: breakdown.investorBonus ?? null,
      sponsorBonus: breakdown.sponsorBonus ?? null,
    } : null,
  }
  return next
}

export function shouldPersistPcapGiftAudit(audit, packageId) {
  if (isProductionCapitalPackageId(packageId)) return true
  if (!audit || typeof audit !== 'object') return false
  if (audit.applicable || audit.error) return true
  return Array.isArray(audit.warnings) && audit.warnings.length > 0
}

/**
 * Re-read payment notes, merge pcap_gift_audit, write back. Non-throwing.
 * @param {any} supabase
 * @param {string} paymentId
 * @param {Record<string, any>} audit
 */
export async function persistPcapGiftAudit(supabase, paymentId, audit) {
  try {
    if (!supabase || !paymentId || !audit) return { ok: false, reason: 'missing_args' }
    const { data, error: readErr } = await supabase
      .from('crypto_payment_transactions')
      .select('transaction_notes')
      .eq('id', paymentId)
      .maybeSingle()
    if (readErr || !data) return { ok: false, reason: readErr?.message || 'payment_missing' }
    const merged = mergePcapGiftAudit(data.transaction_notes, audit)
    const { error } = await supabase
      .from('crypto_payment_transactions')
      .update({ transaction_notes: merged })
      .eq('id', paymentId)
    if (error) return { ok: false, reason: error.message }
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: e?.message || String(e) }
  }
}

export function listActiveProductionCapitalPackages() {
  return Object.values(PRODUCTION_CAPITAL_PACKAGES)
    .filter((pkg) => pkg.active)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * @param {ProductionCapitalPackageId} id
 */
export function getProductionCapitalPackage(id) {
  if (!isProductionCapitalPackageId(id)) return null;
  const pkg = PRODUCTION_CAPITAL_PACKAGES[id];
  return pkg && pkg.active ? pkg : null;
}

export function packageInventoryUsed(pkg) {
  return pkg.paidShares + pkg.investorBonus + pkg.sponsorBonus;
}

/**
 * v4.8.257: payment_methods that are NOT a real purchase, and therefore must never
 * decrement Counter A (the 250,000 paid-share cap). Campaign matrix §5:
 * "Only PAID SHARES reduce this counter. Investor bonus shares and sponsor bonus shares
 * MUST NOT reduce the 250,000 campaign counter."
 *
 * Three families are excluded:
 *   1. bonus / gift grants — including 'Consolidated Bonus', which is where consolidation
 *      REWRITES bonus shares (it moves them out of `bonus_shares` and into
 *      `shares_purchased`, which is exactly how bonus was leaking into the paid counter);
 *   2. commission credits — COMMISSION_LEVEL_*, COMMISSION_FIX_*, conversions;
 *   3. migration / transfer artefacts — consolidations, legacy, glitch corrections,
 *      received transfers, and Performance Pool payouts.
 * These still consume real inventory, so they remain in Counter B.
 */
const CAMPAIGN_NON_PAID_METHODS = new Set([
  'Consolidated',
  'Consolidated Remainder',
  'Consolidated Bonus',
  'Legacy Bonus',
  'Glitch Correction',
  'Received Transfers',
  'TRANSFER_RECEIVED_NO_COMMISSION',
  'Commission Shares',
  'Performance Pool',
  'Gold Diggers Pool Claim',
  'USDT_COMMISSION_CONVERSION',
  'ACRES_REDEMPTION',
]);

/** True when a purchase row represents money actually paid for shares in this campaign. */
export function isCampaignPaidShareMethod(method) {
  const m = String(method || '').trim();
  if (!m) return false;
  if (CAMPAIGN_NON_PAID_METHODS.has(m)) return false;
  const upper = m.toUpperCase();
  // Commission credits of every generation (COMMISSION_LEVEL_*, COMMISSION_FIX_*,
  // COMMISSION_CORRECTION_*, 'commission_conversion', USDT_COMMISSION_CONVERSION).
  if (upper.startsWith('COMMISSION') || upper.includes('COMMISSION_CONVERSION')) return false;
  // Every promotional grant marker ends in _GIFT (PCAP_2026_BUYER_GIFT,
  // SINGLE_SPECIAL_2026_UPLINE_GIFT, BIRTHDAY_2026_PRESALE_GIFT, ...), and the
  // SINGLE_SPECIAL_2026_CORRECTION reversals must not credit the cap either.
  if (upper.endsWith('_GIFT') || upper.endsWith('_CORRECTION')) return false;
  return true;
}

/**
 * @param {ProductionCapitalPackageId} packageId
 * @param {number} qty
 */
export function computeProductionCapitalBreakdown(packageId, qty = 1) {
  const pkg = getProductionCapitalPackage(packageId);
  if (!pkg) return null;
  const q = Math.max(1, Math.min(PRODUCTION_CAPITAL_CAMPAIGN.maxPackageQty || 100, Math.floor(Number(qty) || 1)));
  return {
    packageId: pkg.id,
    name: pkg.name,
    qty: q,
    unitPaidShares: pkg.paidShares,
    unitPriceUsd: pkg.priceUsd,
    unitInvestorBonus: pkg.investorBonus,
    unitSponsorBonus: pkg.sponsorBonus,
    paidShares: pkg.paidShares * q,
    priceUsd: pkg.priceUsd * q,
    investorBonus: pkg.investorBonus * q,
    investorTotal: (pkg.paidShares + pkg.investorBonus) * q,
    sponsorBonus: pkg.sponsorBonus * q,
    inventoryUsed: packageInventoryUsed(pkg) * q,
  };
}

/**
 * Commission-conversion quote. Share counts come from the matrix.
 * Dollar cost is paid shares × the live phase price, not the matrix priceUsd.
 * qty must already be an integer in range — this does not clamp a bad qty up to 1.
 *
 * @param {ProductionCapitalPackageId} packageId
 * @param {number} qty
 * @param {number} phasePrice
 */
export function quoteProductionCapitalConversion(packageId, qty, phasePrice) {
  const price = Number(phasePrice);
  const q = Number(qty);
  const maxQty = PRODUCTION_CAPITAL_CAMPAIGN.maxPackageQty || 100;
  if (!Number.isFinite(price) || price <= 0) return null;
  if (!Number.isInteger(q) || q < 1 || q > maxQty) return null;
  const breakdown = computeProductionCapitalBreakdown(packageId, q);
  if (!breakdown || breakdown.qty !== q) return null;
  const costUsd = breakdown.paidShares * price;
  if (!Number.isFinite(costUsd) || costUsd <= 0) return null;
  return { ...breakdown, phasePrice: price, costUsd };
}

/**
 * One row per package the balance can buy at least once, quoted at max quantity.
 * @param {number} balance
 * @param {number} phasePrice
 */
export function listAffordableProductionCapitalConversions(balance, phasePrice) {
  const bal = Number(balance);
  const price = Number(phasePrice);
  if (!Number.isFinite(bal) || bal <= 0 || !Number.isFinite(price) || price <= 0) return [];
  const out = [];
  for (const pkg of listActiveProductionCapitalPackages()) {
    const unit = quoteProductionCapitalConversion(pkg.id, 1, price);
    if (!unit || unit.costUsd > bal + 1e-8) continue;
    const maxQty = Math.min(
      PRODUCTION_CAPITAL_CAMPAIGN.maxPackageQty || 100,
      Math.floor((bal + 1e-8) / unit.costUsd),
    );
    if (maxQty < 1) continue;
    const atMax = quoteProductionCapitalConversion(pkg.id, maxQty, price);
    if (!atMax || atMax.costUsd > bal + 1e-8) continue;
    out.push({ ...atMax, maxQty });
  }
  return out;
}

/**
 * Default choice: highest buyer bonus, then sponsor bonus, then amount spent.
 * @param {number} balance
 * @param {number} phasePrice
 */
export function recommendProductionCapitalConversion(balance, phasePrice) {
  let best = null;
  for (const quote of listAffordableProductionCapitalConversions(balance, phasePrice)) {
    if (
      !best
      || quote.investorBonus > best.investorBonus
      || (quote.investorBonus === best.investorBonus && quote.sponsorBonus > best.sponsorBonus)
      || (quote.investorBonus === best.investorBonus && quote.sponsorBonus === best.sponsorBonus && quote.costUsd > best.costUsd)
    ) {
      best = quote;
    }
  }
  return best;
}

/**
 * One order of distinct packages. Qty 0 lines are omitted by the caller.
 * Dollar cost is paid shares × the live phase price.
 * @param {Array<{ packageId?: string, qty?: number }>} lines
 * @param {number} phasePrice
 */
export function quoteProductionCapitalCart(lines, phasePrice) {
  if (!Array.isArray(lines) || lines.length === 0 || lines.length > 5) return null;
  const price = Number(phasePrice);
  if (!Number.isFinite(price) || price <= 0) return null;
  const seen = new Set();
  const quoted = [];
  for (const line of lines) {
    const id = line?.packageId;
    const qty = Math.floor(Number(line?.qty));
    if (!isProductionCapitalPackageId(id) || seen.has(id)) return null;
    if (!Number.isInteger(qty) || qty < 1) return null;
    seen.add(id);
    const quote = quoteProductionCapitalConversion(id, qty, price);
    if (!quote) return null;
    quoted.push(quote);
  }
  const sum = (key) => quoted.reduce((total, row) => total + row[key], 0);
  return {
    lines: quoted,
    phasePrice: price,
    costUsd: sum('costUsd'),
    paidShares: sum('paidShares'),
    investorBonus: sum('investorBonus'),
    investorTotal: sum('investorTotal'),
    sponsorBonus: sum('sponsorBonus'),
    inventoryUsed: sum('inventoryUsed'),
  };
}

/**
 * USDT still needed after balance and pending top-ups, rounded up to the cent.
 * @param {number} costUsd
 * @param {number} balance
 * @param {number} [pending]
 */
/**
 * Cart saved on a purchase. It counts only when paid shares and USD match the payment.
 * @param {Record<string, any>|null|undefined} notes
 * @param {unknown} paidShares
 * @param {unknown} amountUsd
 */
export function readExplicitPcapCart(notes) {
  if (!notes || notes.campaign_cart_explicit !== true) return null
  const raw = Array.isArray(notes.campaign_packages) ? notes.campaign_packages : []
  const lines = raw.filter((line) => Math.floor(Number(line?.qty)) > 0)
  if (lines.length === 0) return null
  return quoteProductionCapitalCart(lines, PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd)
}

export function matchExplicitPcapCart(notes, paidShares, amountUsd, { allowOverpay = false } = {}) {
  const cart = readExplicitPcapCart(notes)
  if (!cart) return null
  const paid = Number(paidShares)
  const amt = Number(amountUsd)
  if (!Number.isFinite(paid) || cart.paidShares !== paid) return null
  if (!Number.isFinite(amt) || amt + 0.009 < cart.costUsd) return null
  if (!allowOverpay && amt - cart.costUsd > PCAP_AMOUNT_TOL) return null
  return cart
}

/**
 * Whole packages that give the most buyer shares without spending more than the budget.
 * Larger packages are preferred because each one pays more buyer bonus per dollar than
 * the smaller packages that add up to the same price. Unspent change stays unspent.
 * @param {number} budgetUsd
 */
export function optimizeProductionCapitalSpend(budgetUsd) {
  const budget = Number(budgetUsd)
  if (!Number.isFinite(budget) || budget < PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd) return null
  let left = Math.floor(budget + 1e-8)
  const order = listActiveProductionCapitalPackages().slice().sort((a, b) => {
    const densityA = (a.paidShares + a.investorBonus) / a.priceUsd
    const densityB = (b.paidShares + b.investorBonus) / b.priceUsd
    return densityB - densityA || b.priceUsd - a.priceUsd
  })
  const lines = []
  const maxQty = PRODUCTION_CAPITAL_CAMPAIGN.maxPackageQty || 100
  for (const pkg of order) {
    const qty = Math.min(maxQty, Math.floor(left / pkg.priceUsd))
    if (qty < 1) continue
    lines.push({ packageId: pkg.id, qty })
    left -= qty * pkg.priceUsd
  }
  const cart = quoteProductionCapitalCart(lines, PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd)
  if (!cart) return null
  return { ...cart, unspentUsd: Math.max(0, budget - cart.costUsd) }
}

/**
 * Compare a chosen cart with the layout that gives the most buyer shares for the same spend.
 * @param {Array<{ packageId?: string, qty?: number }>} lines
 */
export function adviseProductionCapitalCart(lines) {
  const cart = quoteProductionCapitalCart(lines, PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd)
  if (!cart) return null
  const better = optimizeProductionCapitalSpend(cart.costUsd)
  if (!better) return { cart, better: null, buyerLoss: 0, sponsorLoss: 0 }
  const buyerLoss = better.investorTotal - cart.investorTotal
  const sponsorLoss = better.sponsorBonus - cart.sponsorBonus
  const sameLayout = better.lines.length === cart.lines.length
    && better.lines.every((line, index) => line.packageId === cart.lines[index].packageId && line.qty === cart.lines[index].qty)
  if (sameLayout || (buyerLoss <= 0 && sponsorLoss <= 0)) {
    return { cart, better: null, buyerLoss: 0, sponsorLoss: 0 }
  }
  return { cart, better, buyerLoss, sponsorLoss }
}

/**
 * @param {Array<{ packageId?: string, qty?: number }>} lines
 * @param {{ paidSharesSold?: number, totalSharesAllocated?: number, now?: Date }} state
 */
export function validateProductionCapitalCart(lines, state = {}) {
  const cart = quoteProductionCapitalCart(lines, PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd)
  if (!cart) return { ok: false, reason: 'invalid_package' }
  if (!isProductionCapitalCampaignActive({ paidSharesSold: state.paidSharesSold, now: state.now })) {
    return { ok: false, reason: 'campaign_inactive' }
  }
  const counters = computeProductionCapitalCounters(state)
  if (cart.paidShares > counters.paidSharesRemaining) {
    return { ok: false, reason: 'insufficient_paid_cap' }
  }
  if (cart.inventoryUsed > counters.actualSharesRemaining) {
    return { ok: false, reason: 'insufficient_actual_inventory' }
  }
  return { ok: true, cart, counters }
}

export function commissionPackageShortfall(costUsd, balance, pending = 0) {
  const raw = Number(costUsd) - Number(balance) - Number(pending);
  if (!Number.isFinite(raw) || raw <= 0.000001) return 0;
  return Math.ceil((raw * 100) - 1e-9) / 100;
}

function forceActiveFromEnv() {
  try {
    if (typeof process !== 'undefined' && process.env?.PRODUCTION_CAPITAL_FORCE_ACTIVE === 'true') return true;
  } catch {
    // noop
  }
  try {
    // Vite browser build
    if (typeof import.meta !== 'undefined' && import.meta?.env?.VITE_PRODUCTION_CAPITAL_FORCE_ACTIVE === 'true') return true;
  } catch {
    // noop
  }
  return false;
}

export function isProductionCapitalDateActive(now = new Date()) {
  if (forceActiveFromEnv()) return true;
  const t = now.getTime();
  return (
    t >= Date.parse(PRODUCTION_CAPITAL_CAMPAIGN.startDate) &&
    t <= Date.parse(PRODUCTION_CAPITAL_CAMPAIGN.endDate)
  );
}

/**
 * @param {{ paidSharesSold?: number, now?: Date }} state
 */
export function isProductionCapitalCampaignActive(state = {}) {
  const now = state.now instanceof Date ? state.now : new Date();
  if (!isProductionCapitalDateActive(now)) return false;
  const paidSold = Number(state.paidSharesSold);
  const sold = Number.isFinite(paidSold) && paidSold >= 0 ? paidSold : 0;
  return sold < PRODUCTION_CAPITAL_CAMPAIGN.paidShareCap;
}

/**
 * @param {{ paidSharesSold?: number, totalSharesAllocated?: number, startingActualInventory?: number }} state
 */
export function computeProductionCapitalCounters(state = {}) {
  const paidSharesSold = Math.max(0, Number(state.paidSharesSold || 0));
  const totalSharesAllocated = Math.max(0, Number(state.totalSharesAllocated || 0));
  // Use DB-provided startingActualInventory if available, else fall back to code constant.
  const startingActualInventory = Math.max(0, Number(state.startingActualInventory) || PRODUCTION_CAPITAL_CAMPAIGN.startingActualInventory);
  return {
    paidShareCap: PRODUCTION_CAPITAL_CAMPAIGN.paidShareCap,
    paidSharesSold,
    paidSharesRemaining: Math.max(0, PRODUCTION_CAPITAL_CAMPAIGN.paidShareCap - paidSharesSold),
    capitalRaisedUsd: paidSharesSold * PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd,
    startingActualInventory,
    totalSharesAllocated,
    actualSharesRemaining: Math.max(0, startingActualInventory - totalSharesAllocated),
  };
}

/**
 * @param {ProductionCapitalPackageId} packageId
 * @param {{ paidSharesSold?: number, totalSharesAllocated?: number, now?: Date }} state
 * @param {number} qty
 */
export function validateProductionCapitalPurchase(packageId, state = {}, qty = 1) {
  const breakdown = computeProductionCapitalBreakdown(packageId, qty);
  if (!breakdown) return { ok: false, reason: 'invalid_package' };

  if (!isProductionCapitalCampaignActive({ paidSharesSold: state.paidSharesSold, now: state.now })) {
    return { ok: false, reason: 'campaign_inactive' };
  }

  const counters = computeProductionCapitalCounters(state);

  if (breakdown.paidShares > counters.paidSharesRemaining) {
    return { ok: false, reason: 'insufficient_paid_cap' };
  }
  if (breakdown.inventoryUsed > counters.actualSharesRemaining) {
    return { ok: false, reason: 'insufficient_actual_inventory' };
  }

  return { ok: true, breakdown, counters };
}

/**
 * Fetch live campaign counters from the DB for server-side validation.
 *
 * Counter A (paid shares sold): campaign-specific — only shares_purchased
 *   from purchases made ON OR AFTER the campaign startDate. This ensures the
 *   250,000 cap starts at 0 when the campaign goes live, not at the
 *   pre-campaign phase shares_sold value.
 *
 * Counter B (actual inventory): startingActualInventory is the phase
 *   total_shares_available. totalSharesAllocated is investment_phases.shares_sold
 *   (already includes PCAP buyer + commission increments). Campaign rows are
 *   NOT subtracted again.
 *
 * @param {any} supabase
 * @param {number} phaseId — active investment phase id
 * @returns {Promise<{ paidSharesSold: number, totalSharesAllocated: number, startingActualInventory: number } | null>}
 */
export async function fetchProductionCapitalState(supabase, phaseId) {
  try {
    const { fetchAllRows } = await import('./fetchAllRows.js');

    // Phase totals — read from DB so startingActualInventory matches live state.
    const { data: phase, error: phaseErr } = await supabase
      .from('investment_phases')
      .select('shares_sold, total_shares_available')
      .eq('id', phaseId)
      .maybeSingle();

    if (phaseErr) {
      console.error('[PCAP] fetchProductionCapitalState phase query error:', phaseErr.message);
      return null;
    }

    const phaseSharesSold = Math.max(0, Number(phase?.shares_sold) || 0);
    const phaseTotalAvailable = Math.max(0, Number(phase?.total_shares_available) || 0);
    // Counter B uses the phase inventory columns once. Approval already increments
    // shares_sold for PCAP buyer + commission shares. Subtracting campaign purchase
    // rows on top of (total - shares_sold) double-counted those allocations and
    // tripped insufficient_actual_inventory before the 250k paid cap (#117).
    const startingActualInventory = phaseTotalAvailable;

    // Campaign-specific share purchases since campaign go-live.
    // Use fetchAllRows to paginate past the 1000-row Supabase default limit.
    const campaignStart = PRODUCTION_CAPITAL_CAMPAIGN.startDate;
    const campaignRows = await fetchAllRows(
      supabase
        .from('aureus_share_purchases')
        .select('shares_purchased, bonus_shares, payment_method')
        .eq('phase_id', phaseId)
        .eq('status', 'active')
        .gte('purchase_date', campaignStart)
        // v4.8.257: stable order — fetchAllRows pages with .range() and PostgreSQL gives
        // no cross-page ordering guarantee without it (see v4.8.251/v4.8.254).
        .order('id', { ascending: true })
    );

    let paidSharesSold = 0;
    let totalSharesAllocated = 0;
    for (const row of campaignRows || []) {
      const paid = Number(row.shares_purchased) || 0;
      const bonus = Number(row.bonus_shares) || 0;
      // Counter B (actual inventory): EVERY allocated share consumes inventory —
      // paid, investor bonus and sponsor bonus alike (campaign matrix §6).
      totalSharesAllocated += paid + bonus;
      // Counter A (the 250,000 paid cap): ONLY genuine purchased shares may decrement it.
      // Campaign matrix §5: "Investor bonus shares and sponsor bonus shares MUST NOT
      // reduce the 250,000 campaign counter."
      // v4.8.257 FIX: this previously added `shares_purchased` for EVERY row in the phase,
      // with no payment-method filter. Consolidation rewrites bonus shares out of
      // `bonus_shares` and into `shares_purchased` on a 'Consolidated Bonus' row, and
      // commission credits are written as COMMISSION_LEVEL_* rows with real
      // `shares_purchased` — so both were silently eating the paid cap. Measured on prod:
      // Counter A read 173.42 when genuine paid shares sold were 84 (85.00 Consolidated
      // Bonus + 4.42 commission wrongly included) — a 106% over-count that would have
      // closed the campaign at roughly half the intended 250,000 paid shares.
      if (isCampaignPaidShareMethod(row.payment_method)) {
        paidSharesSold += paid;
      }
    }

    return {
      paidSharesSold,
      totalSharesAllocated: phaseSharesSold,
      campaignRowAllocated: totalSharesAllocated,
      startingActualInventory,
    };
  } catch (e) {
    console.error('[PCAP] fetchProductionCapitalState fatal:', e?.message || String(e));
    return null;
  }
}
